import { PLACE_SUBSCRIPTION_TYPE } from '@/constants';
import { __ } from '@/locale';
import { SubscriptionType } from '@/types/place';
import { Place } from '@/types/state/shared';
import { secondsToHour } from './date';
import { isEmpty } from './objects';
import { server } from './serverRequests';
import { promiseWrapper } from './utils';

export const getImages = (place: Place) => {
  const { profileImage, images = [], settings } = place.about || {};
  const galleryImages = [...images];
  if (!galleryImages.length) {
    if (profileImage) {
      galleryImages.push(profileImage);
    } else {
      return null;
    }
  }

  return settings?.subscriptionType !== 'essential' && settings?.subscriptionType !== 'mini'
    ? galleryImages
    : galleryImages.slice(0, 3);
};

export const getServices = ({ serviceIds, place }: { serviceIds: Array<number>; place: any }) => {
  const services = [];

  if (!Array.isArray(place.services)) {
    return null;
  }

  place.services.forEach((category) => {
    if (!Array.isArray(category.services)) {
      return;
    }

    category.services.forEach((service) => {
      if (!service.id) {
        return;
      }

      serviceIds.forEach((serviceId) => {
        if (serviceId === service.id) {
          services.push(service);
        }
      });
    });
  });

  return services.length > 0 ? services : null;
};

export const getEmployee = ({ employeeId, place }: { employeeId: number; place: any }) => {
  if (!Array.isArray(place.employees)) {
    return null;
  }
  return place.employees.filter((employee) => employee.id === employeeId)[0];
};

export const fetchPlace = async ({ id, slug, abortSignal }: { id: number; slug: string; abortSignal: AbortSignal }) => {
  if (!id || !slug) {
    return null;
  }

  const { data } = await promiseWrapper(
    server.request.get(`/findPlace/${id}/${encodeURIComponent(slug)}?slug=${encodeURIComponent(slug)}`, {
      abortSignal,
    }),
  );

  const fetchedPlace = data.data.place;

  return fetchedPlace;
};

export const formatPhoneNumber = (phoneNumber: string) => {
  if (phoneNumber.length === 10) {
    return {
      fullNumber: `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6, 8)} ${phoneNumber.slice(
        8,
        10,
      )}`,
      hiddenNumber: `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6, 8)} XX`,
    };
  } else if (phoneNumber.length > 2) {
    return {
      fullNumber: phoneNumber,
      hiddenNumber: phoneNumber.slice(0, -2) + 'XX',
    };
  }
  return {
    fullNumber: phoneNumber,
    hiddenNumber: phoneNumber,
  };
};

export const getBreadcrumbItems = (place: any): { title: string; url: string }[] => {
  const mainCategory = place.about?.category?.main;
  const subCategory = place.about?.category?.sub;

  const items = [
    { title: __('Home'), url: '/' },
    ...(mainCategory ? [{ title: mainCategory, url: `/${mainCategory}/var` }] : []),
    ...(subCategory ? [{ title: subCategory, url: `/${subCategory}/var` }] : []),
  ];

  return items;
};

type OpeningHours = Record<string, { from?: number; to?: number }[]>;

export const getOpenHoursStrings = (openingHours: OpeningHours) => {
  const WEEKDAYS = {
    '1': 'monday',
    '2': 'tuesday',
    '3': 'wednesday',
    '4': 'thursday',
    '5': 'friday',
    '6': 'saturday',
    '0': 'sunday',
  };

  const openHours = Object.entries(WEEKDAYS).reduce((acc, [key, value]) => {
    const hours = openingHours?.[key];

    if (!hours || hours.length === 0) {
      return acc;
    }

    acc[value] = `${secondsToHour(hours[0].from)} - ${secondsToHour(hours[0].to)}`;
    return acc;
  }, {});

  if (isEmpty(openHours)) {
    return undefined;
  }
  return openHours;
};

export const getMerchantKlarnaClientId = (place: Place) => {
  const { employees } = place;
  return employees?.find((employee) => !!employee.about.settings?.klarnaClientId)?.about?.settings?.klarnaClientId;
};

export const getGiftcardLabel = (place: Place) => {
  if (place.about.settings.sellsGiftCard && place.about.settings.sellsValueCard) {
    return __('giftcardLabel') + ' / ' + __('valuecardLabel');
  }

  if (place.about.settings.sellsGiftCard) {
    return __('giftcardLabel');
  }

  if (place.about.settings.sellsValueCard) {
    return __('valuecardLabel');
  }

  return '';
};

export const getGiftcardCheckoutLink = (place: Place) => {
  if (!place.about?.settings?.sellsGiftCard && !place.about?.settings?.sellsValueCard) {
    return null;
  }
  let url = `/places/${place.about.slug}-${place.id}`;

  if (place.about.settings.sellsValueCard) {
    url += '/valuecards/buy';
  } else {
    url += '/giftcard/checkout';
  }
  return url;
};

export const getUrlWithProtocol = (oldUrl: string) => {
  let url = (oldUrl || '').trim();
  const hasProtocol = /^(https?:\/\/)/i.test(url);
  const hasWhitespace = /\s/.test(url);
  if (url && !hasWhitespace && !hasProtocol) {
    url = 'http://' + url;
  }
  return url;
};

export function isProfilePageSubscription(type: SubscriptionType): boolean {
  return (
    type === PLACE_SUBSCRIPTION_TYPE.PROFILE_PAGE_PREMIUM || type === PLACE_SUBSCRIPTION_TYPE.PROFILE_PAGE_VERIFIED
  );
}
